import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import ContentPageHero from '../components/ContentPageHero';
import TrustedBy from '../components/TrustedBy';
import Footer from '../components/Footer';

const AboutPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About | PH Therapy</title>
        <meta name="description" content="Our AHCS-registered team of clinical exercise physiologists bridge the gap between physiotherapy and strength & conditioning. Expert care for partners, CEOs, and professionals. London, Wimbledon, Fulham, Kensington, Chiswick, Surrey & Sussex." />
        <link rel="canonical" href="https://www.pandhtherapy.co.uk/about" />
      </Helmet>
      <Header />
      <main id="about-page" className="about-page">
        <ContentPageHero
          title="About PH Therapy"
          description="Our team of AHCS Registered Clinical Exercise Physiologists bridge the gap between physiotherapy and strength & conditioning—delivering bespoke, evidence-based programmes for partners, CEOs, and corporate leaders. We serve clients across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex."
          backgroundImage={`${process.env.PUBLIC_URL}/TeamImage.JPG`}
        />

        <section className="about-page-section about-page-intro">
          <div className="container about-page-row">
            <div className="about-page-block about-page-block--text">
              <h2>Bridging the Gap</h2>
              <p className="about-page-lead">
                PH Therapy empowers high-performing individuals—CEOs, partners, and corporate leaders—to live pain-free, active lives.
              </p>
              <p>
                Combining science-backed techniques with personalised care, we help clients recover, strengthen, and thrive. Whether returning to sport, overcoming chronic pain, or achieving peak physical performance, our trusted team delivers programmes that improve health outcomes and enhance quality of life.
              </p>
              <Link to="/contact" className="about-page-cta">Get in touch</Link>
            </div>
            <div className="about-page-block about-page-block--image">
              <img
                src={`${process.env.PUBLIC_URL}/ExerciseWarmUp.png`}
                alt="Clinical exercise physiology session with one-on-one coaching"
                className="about-page-image"
              />
            </div>
          </div>
        </section>

        <section className="about-page-section about-page-section--alt">
          <TrustedBy variant="categories" />
        </section>

        <section className="about-page-section">
          <div className="container">
            <h2 className="about-page-section-title">Our Expertise</h2>
            <p className="about-page-intro-text">
              As the most qualified professionals for clinical exercise prescription, our CEPs are dedicated to delivering the highest standard of care to support your long-term well-being.
            </p>
            <div className="about-page-features">
              <div className="about-page-feature">
                <div className="about-page-feature-icon">
                  <i className="fas fa-user-md" aria-hidden="true" />
                </div>
                <h3>AHCS Registered</h3>
                <p>Our team holds advanced certifications in clinical exercise physiology and rehabilitation.</p>
              </div>
              <div className="about-page-feature">
                <div className="about-page-feature-icon">
                  <i className="fas fa-heart" aria-hidden="true" />
                </div>
                <h3>Personalised Care</h3>
                <p>Tailored programmes designed for your unique health goals, lifestyle, and needs.</p>
              </div>
              <div className="about-page-feature">
                <div className="about-page-feature-icon">
                  <i className="fas fa-flask" aria-hidden="true" />
                </div>
                <h3>Evidence-Based</h3>
                <p>Science-backed protocols for musculoskeletal (MSK), oncology, cardiac, chronic conditions, and lifestyle management.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="about-page-section">
          <div className="container about-page-row">
            <div className="about-page-block about-page-block--image about-page-block--order-first">
              <img
                src={`${process.env.PUBLIC_URL}/CorporateSpeaking.jpg`}
                alt="PH Therapy corporate speaking and professional consultation"
                className="about-page-image"
              />
            </div>
            <div className="about-page-block about-page-block--text">
              <h2>Our Mission</h2>
              <p className="about-page-lead">
                We aim to empower our clients with confidence, energy, and control over their health.
              </p>
              <p>
                Fitness and wellness are deeply personal journeys. Improving quality of life isn't just about physical outcomes—it's about helping you feel stronger, more capable, and in control. Through personal attention, measurable results, and compassionate care, we're the trusted partner for professionals seeking to reclaim their health.
              </p>
              <ul className="about-page-list">
                <li>Regain energy and improve mobility</li>
                <li>Reduce stress through tailored programmes</li>
                <li>Achieve measurable, sustainable results</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="about-page-section about-page-section--alt">
          <div className="container">
            <h2 className="about-page-section-title">Specialists in MSK, Oncology, Cardiac & Lifestyle Management</h2>
            <div className="about-page-narrow">
              <p>
                We support clients with clinical, chronic, and complex health needs—including musculoskeletal conditions, cancer rehabilitation, cardiac care, and lifestyle management. As a high-end service, we work primarily with partners, CEOs, and senior professionals. We provide compassionate, expert care across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex.
              </p>
            </div>
            <Link to="/services" className="about-page-cta">View our services</Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;
